import { faIcon } from "@utils/faIcon"

import { Avatar } from "@atoms"

import {
    CurrentBalanceBadge,
    LabelsBadges,
    LastModifiedBadge,
    OpenPurchaseOrdersBadge,
    TermsBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { VendorCardProps } from "./VendorCard.types"

const vendorIcon = faIcon({ name: "store", style: "solid" })

export default function VendorCard(props: VendorCardProps) {
    const { vendor, displayedAttributes, onClick, isLoading = false, isTabbable, className, ...errorProps } = props

    // Current balance and open POs always render when selected (even at zero).
    const showCurrentBalance = shouldShowAttribute(displayedAttributes, "outstanding_commitments")
    const showOpenPurchaseOrders = shouldShowAttribute(displayedAttributes, "current_purchase_order_count")
    const showTerms = shouldShowAttribute(displayedAttributes, "payment_terms") && vendor.payment_terms != null
    const showLastModified =
        shouldShowAttribute(displayedAttributes, "last_modified_at") && Boolean(vendor.last_modified_at)
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && vendor.labels.length > 0

    const hasAttributes = showCurrentBalance || showOpenPurchaseOrders || showTerms || showLastModified || showLabels

    return (
        <ObjectCard
            onClick={onClick}
            isLoading={isLoading}
            isTabbable={isTabbable}
            className={className}
            {...errorProps}
        >
            <ObjectCard.Header
                id={vendor.account_id}
                idPlaceholder="No account ID"
                statusBadge={<ObjectStatusBadge objectType="vendor" status={vendor.status_label} size="md" />}
            />
            <ObjectCard.Body
                avatar={<Avatar variant="object" icon={vendorIcon} size="xl" />}
                primaryText={vendor.name}
                secondaryText={vendor.billing_address_formatted ?? undefined}
                secondaryTextPlaceholder="No billing address"
            />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showCurrentBalance && <CurrentBalanceBadge balance={vendor.outstanding_commitments} />}
                    {showOpenPurchaseOrders && <OpenPurchaseOrdersBadge count={vendor.current_purchase_order_count} />}
                    {showTerms && (
                        <TermsBadge
                            variant="fixed"
                            netDays={Number(vendor.payment_terms)}
                            label="Default payment terms"
                        />
                    )}
                    {showLastModified && <LastModifiedBadge date={vendor.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={vendor.labels} />}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
